import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  CreditCard, 
  CheckCircle, 
  Calendar, 
  ArrowRight, 
  Shield, 
  Zap, 
  Users, 
  Brain,
  Clock,
  AlertTriangle,
  Loader2
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { useAuth } from '../../hooks/useAuth'
import { createCheckoutSession, getUserSubscription, getCustomerPortalUrl } from '../../lib/stripe'
import { products } from '../../lib/stripe-config'
import toast from 'react-hot-toast'

export const SubscriptionSettings: React.FC = () => {
  const { appUser } = useAuth()
  const [loading, setLoading] = useState(true)
  const [subscribing, setSubscribing] = useState(false)
  const [subscription, setSubscription] = useState<any>(null)
  const [managingSubscription, setManagingSubscription] = useState(false)

  useEffect(() => {
    loadSubscriptionData()
    
    // Check for success or canceled URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('success') === 'true') {
      toast.success('Subscription created successfully!')
    } else if (urlParams.get('canceled') === 'true') {
      toast.error('Subscription checkout was canceled')
    }
    
    // Clean up URL parameters
    if (urlParams.has('success') || urlParams.has('canceled')) {
      const newUrl = window.location.pathname + (urlParams.get('tab') ? `?tab=${urlParams.get('tab')}` : '')
      window.history.replaceState({}, document.title, newUrl)
    }
  }, [appUser])

  const loadSubscriptionData = async () => {
    try {
      setLoading(true)
      
      // Load subscription data
      const { subscription: subData, error } = await getUserSubscription()
      
      if (error) {
        console.error('Error loading subscription:', error)
        toast.error('Failed to load subscription information')
        return
      }
      
      setSubscription(subData)
    } catch (error) {
      console.error('Error loading subscription data:', error)
      toast.error('Failed to load subscription information')
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribe = async (priceId: string, mode: 'subscription' | 'payment' = 'subscription') => {
    try {
      setSubscribing(true)
      
      // Create checkout session
      const result = await createCheckoutSession(priceId, mode)
      
      if (!result.success) {
        toast.error('Failed to create checkout session')
      }
    } catch (error) {
      console.error('Error creating checkout session:', error)
      toast.error('Failed to process subscription')
    } finally {
      setSubscribing(false)
    }
  }

  const handleManageSubscription = async () => {
    try {
      setManagingSubscription(true)
      
      // Get customer portal URL
      const { url, error } = await getCustomerPortalUrl()
      
      if (error || !url) {
        toast.error('Failed to open customer portal')
        return
      }
      
      // Open customer portal in new tab
      window.open(url, '_blank')
    } catch (error) {
      console.error('Error opening customer portal:', error)
      toast.error('Failed to open customer portal')
    } finally {
      setManagingSubscription(false)
    }
  }

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'N/A'
    return new Date(timestamp * 1000).toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-sage-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading subscription information...</p>
        </div>
      </div>
    )
  }

  const isSubscriptionActive = subscription?.subscription_status === 'active' || 
                              subscription?.subscription_status === 'trialing'

  return (
    <div className="space-y-8">
      {/* Current Subscription */}
      {subscription && isSubscriptionActive && (
        <Card className="p-6 bg-gradient-to-r from-sage-50 to-emerald-50 border-emerald-200">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center">
                <h3 className="text-lg font-semibold text-slate-900 mr-3">
                  Active Subscription
                </h3>
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                  {subscription.product?.name || 'Premium'} Plan
                </span>
              </div>
              
              <div className="mt-2 space-y-2">
                <div className="flex items-center text-sm text-slate-600">
                  <Calendar className="w-4 h-4 mr-2 text-slate-500" />
                  <span>
                    Renews on {formatDate(subscription.current_period_end)}
                  </span>
                </div>
                
                {subscription.payment_method_last4 && (
                  <div className="flex items-center text-sm text-slate-600">
                    <CreditCard className="w-4 h-4 mr-2 text-slate-500" />
                    <span>
                      {subscription.payment_method_brand?.toUpperCase()} •••• {subscription.payment_method_last4}
                    </span>
                  </div>
                )}
                
                {subscription.cancel_at_period_end && (
                  <div className="flex items-center text-sm text-amber-600">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    <span>Your subscription will cancel at the end of the current period</span>
                  </div>
                )}
              </div>
              
              <div className="mt-4">
                <Button
                  onClick={handleManageSubscription}
                  loading={managingSubscription}
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  Manage Subscription
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Subscription Plans */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-6">
          Subscription Plans
        </h3>
        
        <div className="grid md:grid-cols-2 gap-8">
          {products.map((plan) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: products.indexOf(plan) * 0.1 }}
              className="flex flex-col"
            >
              <Card className={`p-6 flex-1 border-2 ${
                isSubscriptionActive && subscription?.product?.id === plan.id
                  ? 'border-sage-500'
                  : 'border-slate-200 hover:border-slate-300'
              } transition-all duration-300`}>
                <div className="mb-4">
                  <h4 className="text-xl font-semibold text-slate-900">
                    {plan.name}
                  </h4>
                  <div className="mt-2">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-slate-900">${plan.price}</span>
                      <span className="text-sm text-slate-500 ml-2">per user/month</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">
                    {plan.description}
                  </p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {plan.features.map((feature: string, index: number) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-sage-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </div>
                  ))}
                </div>
                
                {isSubscriptionActive && subscription?.product?.id === plan.id ? (
                  <Button
                    disabled
                    className="w-full bg-sage-100 text-sage-700 cursor-not-allowed"
                  >
                    Current Plan
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleSubscribe(plan.priceId, plan.mode as 'subscription' | 'payment')}
                    loading={subscribing}
                    className="w-full bg-sage-600 hover:bg-sage-700 text-white"
                  >
                    Subscribe
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Payment Security */}
      <Card className="p-6 bg-slate-50 border-slate-200">
        <div className="flex items-center mb-4">
          <Shield className="w-6 h-6 text-slate-600 mr-3" />
          <h3 className="text-lg font-semibold text-slate-900">
            Secure Payments
          </h3>
        </div>
        
        <p className="text-sm text-slate-600 mb-4">
          All payments are processed securely through Stripe. Your payment information is never stored on our servers.
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-slate-500">
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
            <span>PCI Compliant</span>
          </div>
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
            <span>256-bit SSL</span>
          </div>
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
            <span>Fraud Protection</span>
          </div>
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
            <span>Secure Checkout</span>
          </div>
        </div>
      </Card>

      {/* FAQ */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          Frequently Asked Questions
        </h3>
        
        <div className="space-y-4">
          <Card className="p-6">
            <h4 className="font-medium text-slate-900 mb-2">
              How does billing work?
            </h4>
            <p className="text-sm text-slate-600">
              You'll be billed monthly based on the number of active employees in your account. 
              You can upgrade, downgrade, or cancel your subscription at any time.
            </p>
          </Card>
          
          <Card className="p-6">
            <h4 className="font-medium text-slate-900 mb-2">
              Can I change plans later?
            </h4>
            <p className="text-sm text-slate-600">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, 
              and your billing will be prorated accordingly.
            </p>
          </Card>
          
          <Card className="p-6">
            <h4 className="font-medium text-slate-900 mb-2">
              Is there a free trial?
            </h4>
            <p className="text-sm text-slate-600">
              Yes, all plans include a 14-day free trial. You won't be charged until the trial period ends, 
              and you can cancel anytime before then.
            </p>
          </Card>
        </div>
      </div>
    </div>
  )
}